import React from 'react';
import { useStrings } from '@/hooks/useStrings';

// Shared header for onboarding + session-flow screens. Left slot is a
// back control (chevron + "Back"), right slot is an optional skip text
// link plus an optional exit-to-home X icon to its left. All slots
// maintain a 44×44 hit target and respect the top safe-area inset.
// Background is transparent so the parent screen's colour shows
// through unchanged.
//
// Props
// - onBack   fn           — called when the back control is tapped
// - onSkip   fn | null    — if omitted, the skip link is not rendered
// - onExit   fn | null    — if provided, render an X icon button to
//                           the LEFT of skip that dismisses the
//                           entire onboarding flow (back to /home).
//                           Distinct from skip — skip advances within
//                           onboarding, exit leaves it altogether.
// - showBack bool (true)  — hide the back control without disturbing
//                           the layout (spacer keeps the right slot
//                           right-aligned)
//
// The component is intentionally pure — it does no routing or state.
// Each host screen owns the semantics of back / skip / exit (step
// decrement, overlay close, Supabase write with a "skipped" flag,
// navigate, etc.) and hands the header plain callbacks.

const styles = {
  header: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 48,
    padding: '4px 8px',
    paddingTop: 'calc(4px + env(safe-area-inset-top, 0px))',
    background: 'transparent',
  },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    padding: '10px 12px',
    cursor: 'pointer',
    color: 'var(--warm-brown, #8B7355)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 500,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    minHeight: 44,
  },
  skipBtn: {
    background: 'none',
    border: 'none',
    padding: '10px 12px',
    cursor: 'pointer',
    color: 'var(--warm-brown, #8B7355)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 500,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    minHeight: 44,
    minWidth: 44,
  },
  // Container for the optional exit + skip buttons. Flex row with a
  // small gap so the X icon and the Skip text both sit at the right
  // edge of the header without colliding.
  rightCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  // Icon-only exit button. Same hit target as the others (44×44),
  // icon at 18×18, slightly muted colour to read as a secondary
  // affordance — exit is a real action but it shouldn't outshine
  // the primary CTA on the screen.
  exitBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    padding: '10px 12px',
    cursor: 'pointer',
    color: 'var(--warm-brown, #8B7355)',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    minHeight: 44,
    minWidth: 44,
  },
  // Invisible placeholder so the remaining control stays at its
  // natural edge of the row when its sibling slot is empty.
  spacer: {
    width: 44,
    height: 44,
    flexShrink: 0,
  },
};

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function OnboardingHeader({ onBack, onSkip, onExit, showBack = true }) {
  const t = useStrings();

  return (
    <div style={styles.header}>
      {showBack ? (
        <button
          type="button"
          style={styles.iconBtn}
          onClick={onBack}
          aria-label={t('back_btn')}
        >
          <ChevronLeft />
          <span>{t('back_btn')}</span>
        </button>
      ) : (
        <span style={styles.spacer} aria-hidden="true" />
      )}

      {(onExit || onSkip) ? (
        <div style={styles.rightCluster}>
          {onExit && (
            <button
              type="button"
              style={styles.exitBtn}
              onClick={onExit}
              aria-label={t('exit_onboarding_aria')}
            >
              <CloseIcon />
            </button>
          )}
          {onSkip && (
            <button
              type="button"
              style={styles.skipBtn}
              onClick={onSkip}
              aria-label={t('skip_btn')}
            >
              {t('skip_btn')}
            </button>
          )}
        </div>
      ) : (
        <span style={styles.spacer} aria-hidden="true" />
      )}
    </div>
  );
}
